import React from "react";
import { Paper, Typography } from "@material-ui/core";
import MaterialTable from "material-table";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AddIcon from "@material-ui/icons/Add";
import PriceListIcon from "@material-ui/icons/MonetizationOn";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import StockIcon from "@material-ui/icons/AllInbox";
import RefreshIcon from "@material-ui/icons/Refresh";
import products from "../../service/products";
import { formatCurrency, formatQuantity, parseDecimal } from "../../libs/format";
import ProductDialog from "./ProductDialog";
import AddProductDialog from "./AddProductDialog";
import ImportProductsDialog from "./ImportProductsDialog";
import PriceTablesDialog from "./PriceTablesDialog";
import StockDialog from "./StockDialog";
import PublishIcon from "@material-ui/icons/Publish";
import ConfirmDialog from "../alertas/Confirm";
import SuccessAlert from "../alertas/SuccessAlert";
import DialogError from "../alertas/DialogError";

// Grade principal de um grupo: linhas = produtos, colunas fixas + uma coluna por
// Tabela de Preço (preço editável inline via cellEditable). Estoque e atributos
// via diálogos. Props: group, onBack().
class PriceGrid extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      priceTables: [],
      rows: [],
      loading: true,
      productDialog: false,
      editingProduct: null,
      addDialog: false,
      importDialog: false,
      priceTablesDialog: false,
      stockProduct: null,
      confirmDelete: null,
      successMessage: "",
      showError: false,
      error: null,
    };
  }

  componentDidMount() {
    this.load();
  }

  load = () => {
    this.setState({ loading: true });
    products
      .getGroupFull(this.props.group.id)
      .then((full) => {
        const priceTables = (full.price_tables || [])
          .slice()
          .sort((a, b) => (a.position || 0) - (b.position || 0));
        const rows = (full.products || []).map((p) =>
          this.flattenProduct(p, priceTables)
        );
        this.setState({ priceTables, rows, loading: false });
      })
      .catch(() => this.setState({ loading: false }));
  };

  flattenProduct = (product, priceTables) => {
    const row = {
      id: product.id,
      name: product.name,
      category: product.category,
      presentation: product.presentation,
      unit_of_measure: product.unit_of_measure,
      package_quantity: product.package_quantity,
      currency: product.currency,
      current_stock: product.current_stock,
      _raw: product,
    };
    const priceByTable = {};
    (product.prices || []).forEach((pr) => {
      priceByTable[pr.price_table_id] = pr.price;
    });
    priceTables.forEach((t) => {
      row[`price_${t.id}`] =
        priceByTable[t.id] == null ? "" : priceByTable[t.id];
    });
    return row;
  };

  buildColumns = () => {
    const columns = [
      { title: "id", field: "id", hidden: true },
      { title: "Nome", field: "name", editable: "never" },
      { title: "Categoria", field: "category", editable: "never" },
      { title: "Apresentação", field: "presentation", editable: "never" },
      { title: "Unidade", field: "unit_of_measure", editable: "never" },
      { title: "Emb.", field: "package_quantity", editable: "never" },
      { title: "Moeda", field: "currency", editable: "never" },
      {
        title: "Saldo",
        field: "current_stock",
        editable: "never",
        render: (row) => formatQuantity(row.current_stock),
      },
    ];
    this.state.priceTables.forEach((table) => {
      columns.push({
        title: table.name,
        field: `price_${table.id}`,
        priceTableId: table.id,
        type: "numeric",
        render: (row) =>
          formatCurrency(row[`price_${table.id}`], row.currency),
      });
    });
    return columns;
  };

  // Edição inline de uma célula de preço -> upsert no produto (contrato §6.4).
  onCellEditApproved = (newValue, oldValue, rowData, columnDef) => {
    return new Promise((resolve, reject) => {
      const priceTableId = columnDef.priceTableId;
      const price = parseDecimal(newValue);
      if (price === null) {
        this.setState({
          showError: true,
          error: {
            title: "Valor inválido",
            message: "Informe um número válido para o preço.",
          },
        });
        reject();
        return;
      }
      products
        .updateProduct(rowData.id, {
          prices: [{ price_table_id: priceTableId, price }],
        })
        .then(() => {
          this.setState({
            rows: this.state.rows.map((r) =>
              r.id === rowData.id ? { ...r, [`price_${priceTableId}`]: price } : r
            ),
          });
          resolve();
        })
        .catch((err) => {
          this.setState({
            showError: true,
            error: {
              title: "Erro ao salvar preço",
              message:
                (err && err.data && err.data.message) ||
                "Não foi possível salvar o preço.",
            },
          });
          reject();
        });
    });
  };

  handleProductSaved = () => {
    this.setState({
      productDialog: false,
      editingProduct: null,
      successMessage: "Produto salvo.",
    });
    this.load();
  };

  // Remove o produto DESTE catálogo (não apaga o produto nem o estoque).
  detachProduct = () => {
    const product = this.state.confirmDelete;
    products
      .detachProductFromGroup(this.props.group.id, product.id)
      .then(() => {
        this.setState({
          confirmDelete: null,
          successMessage: "Produto removido do catálogo.",
        });
        this.load();
      })
      .catch((err) => {
        this.setState({
          confirmDelete: null,
          showError: true,
          error: {
            title: "Erro ao remover",
            message: (err && err.data && err.data.message) || "Falha ao remover.",
          },
        });
      });
  };

  updateStockLocally = (productId, currentStock) => {
    this.setState({
      rows: this.state.rows.map((r) =>
        r.id === productId ? { ...r, current_stock: currentStock } : r
      ),
    });
  };

  render() {
    return (
      <Paper>
        {this.state.productDialog && (
          <ProductDialog
            group={this.props.group}
            priceTables={this.state.priceTables}
            product={this.state.editingProduct}
            close={() =>
              this.setState({ productDialog: false, editingProduct: null })
            }
            save={this.handleProductSaved}
          />
        )}
        {this.state.addDialog && (
          <AddProductDialog
            group={this.props.group}
            priceTables={this.state.priceTables}
            close={() => this.setState({ addDialog: false })}
            save={() =>
              this.setState({ addDialog: false, successMessage: "Produto adicionado ao catálogo." }, this.load)
            }
          />
        )}
        {this.state.importDialog && (
          <ImportProductsDialog
            group={this.props.group}
            priceTables={this.state.priceTables}
            close={() => this.setState({ importDialog: false })}
            onImported={this.load}
          />
        )}
        {this.state.priceTablesDialog && (
          <PriceTablesDialog
            group={this.props.group}
            close={() => this.setState({ priceTablesDialog: false })}
            onChanged={this.load}
          />
        )}
        {this.state.stockProduct && (
          <StockDialog
            product={this.state.stockProduct}
            close={() => this.setState({ stockProduct: null })}
            onChanged={(currentStock) =>
              this.updateStockLocally(this.state.stockProduct.id, currentStock)
            }
          />
        )}
        {this.state.confirmDelete && (
          <ConfirmDialog
            title="Atenção"
            body={`Remover o produto "${this.state.confirmDelete.name}" deste catálogo? Ele continua disponível na empresa e nos demais catálogos.`}
            handleCloseConfirmT={this.detachProduct}
            handleCloseConfirmF={() => this.setState({ confirmDelete: null })}
          />
        )}
        <SuccessAlert
          open={!!this.state.successMessage}
          message={this.state.successMessage}
          onClose={() => this.setState({ successMessage: "" })}
        />
        <DialogError
          open={this.state.showError}
          onClose={() => this.setState({ showError: false })}
          title={this.state.error ? this.state.error.title : "Erro"}
          message={this.state.error ? this.state.error.message : ""}
          suggestion={"Verifique os dados e tente novamente."}
        />
        <MaterialTable
          title={this.props.group.name}
          columns={this.buildColumns()}
          data={this.state.rows}
          isLoading={this.state.loading}
          options={{ search: true, paging: true, actionsColumnIndex: -1 }}
          cellEditable={{ onCellEditApproved: this.onCellEditApproved }}
          localization={{
            header: { actions: "Ações" },
            body: { emptyDataSourceMessage: "Nenhum produto cadastrado." },
          }}
          actions={[
            {
              icon: () => <ArrowBackIcon />,
              tooltip: "Voltar aos grupos",
              isFreeAction: true,
              onClick: () => this.props.onBack(),
            },
            {
              icon: () => <AddIcon />,
              tooltip: "Adicionar produto (existente ou novo)",
              isFreeAction: true,
              onClick: () => this.setState({ addDialog: true }),
            },
            {
              icon: () => <PublishIcon />,
              tooltip: "Importar planilha",
              isFreeAction: true,
              onClick: () => this.setState({ importDialog: true }),
            },
            {
              icon: () => <PriceListIcon />,
              tooltip: "Gerenciar tabelas de preço",
              isFreeAction: true,
              onClick: () => this.setState({ priceTablesDialog: true }),
            },
            {
              icon: () => <RefreshIcon />,
              tooltip: "Atualizar",
              isFreeAction: true,
              onClick: this.load,
            },
            {
              icon: () => <EditIcon />,
              tooltip: "Editar produto",
              onClick: (event, row) =>
                this.setState({ productDialog: true, editingProduct: row._raw }),
            },
            {
              icon: () => <StockIcon />,
              tooltip: "Estoque",
              onClick: (event, row) => this.setState({ stockProduct: row._raw }),
            },
            {
              icon: () => <DeleteIcon />,
              tooltip: "Remover deste catálogo",
              onClick: (event, row) => this.setState({ confirmDelete: row }),
            },
          ]}
        />
        {this.state.priceTables.length === 0 && !this.state.loading && (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ padding: 16 }}
          >
            Este grupo ainda não tem tabelas de preço. Use "Gerenciar tabelas de
            preço" para criar as colunas (ex.: Tabela 1, Tabela 2).
          </Typography>
        )}
      </Paper>
    );
  }
}

export default PriceGrid;
